import { z } from "zod";

/**
 * switchOverridesSchema is the optional display-override subset a postMessage
 * content switch may carry alongside the target itinerary. Fields are camelCase to
 * match the postMessage convention; omitted fields leave the current studio state
 * unchanged. The array overrides (withOverlays, withTerrains) additionally tolerate
 * an explicit null — a host that clears an override by sending null is treated the
 * same as omitting it (leave unchanged), so a JSON `null` never rejects the whole
 * content-switch message. Validated because the message arrives from an untrusted
 * parent window.
 */
export const switchOverridesSchema = z.object({
  terrain: z.string().optional(),
  withDirectionsControl: z.boolean().optional(),
  withDownloadControl: z.boolean().optional(),
  withNavigationControl: z.boolean().optional(),
  withOverlays: z.array(z.string()).nullable().optional(),
  withOverlaysControl: z.boolean().optional(),
  withPreferencesControl: z.boolean().optional(),
  withProfileAnalysis: z.boolean().optional(),
  withProfileCharacteristics: z.boolean().optional(),
  withProfileElevation: z.boolean().optional(),
  withStreetviewControl: z.boolean().optional(),
  withTerrains: z.array(z.string()).nullable().optional(),
});

/**
 * SwitchOverrides is the optional display-override subset, derived from
 * switchOverridesSchema.
 */
export type SwitchOverrides = z.infer<typeof switchOverridesSchema>;

/**
 * embedSetContentInput is the tool input the inbound `setItinerary` bridge message maps
 * to: the target itinerary id plus the optional display overrides — i.e. the inbound
 * message shape minus its `type` discriminant. The bridge dispatches
 * studio.embed.setContent with this, and the tool validates against it, so the postMessage
 * boundary and the tool boundary check the exact same shape.
 */
export const embedSetContentInput = switchOverridesSchema.extend({
  itineraryId: z.string(),
});

/**
 * SET_ITINERARY_MESSAGE is the inbound message type that activates an itinerary.
 */
export const SET_ITINERARY_MESSAGE = "mountaya:setItinerary";

/**
 * READY_MESSAGE is the outbound message type the embed posts to its parent once
 * the map has finished loading (or settled with an error).
 */
export const READY_MESSAGE = "mountaya:ready";

/**
 * REQUEST_CAMERA_MESSAGE is the inbound message type a parent posts to ask the
 * embedded studio for its live camera (center + zoom/bearing/pitch). The embed
 * wizard's "Set current view as default" sends it to its same-origin preview iframe;
 * the embed answers with a CAMERA_MESSAGE. It carries no payload — the type alone is
 * the request — so the parent reads the current map state without driving it.
 */
export const REQUEST_CAMERA_MESSAGE = "mountaya:requestCamera";

/**
 * CAMERA_MESSAGE is the outbound message type the embed posts back to its parent
 * in reply to a REQUEST_CAMERA_MESSAGE, carrying the live camera the publisher
 * panned/tilted/zoomed the preview to. The wizard stores it as the embed's default
 * view.
 */
export const CAMERA_MESSAGE = "mountaya:camera";

/**
 * requestCameraMessageSchema validates the (payload-free) camera request. The
 * embed Zod-parses every window message against it before reading the map, so a
 * stray same-origin message cannot trip the reply path — only the exact type does.
 */
export const requestCameraMessageSchema = z.object({
  type: z.literal(REQUEST_CAMERA_MESSAGE),
});

/**
 * RequestCameraMessage is the validated inbound camera request, derived from
 * requestCameraMessageSchema.
 */
export type RequestCameraMessage = z.infer<typeof requestCameraMessageSchema>;

/**
 * cameraMessageSchema is the outbound camera reply: the live `center` ([lng, lat]),
 * `zoom`, `bearing` (degrees, 0 = north), and `pitch` (degrees) the map reads from
 * itself — the FULL camera anchor the publisher framed, so the wizard stores the
 * exact view rather than just the angle overlaid on the route's center. The wizard
 * Zod-parses the reply at the boundary (the message rides a same-origin postMessage,
 * but it is still validated like every cross-frame payload) before storing it as the
 * default view; the wizard clamps the channels to its own UI ranges when it stores them.
 */
export const cameraMessageSchema = z.object({
  bearing: z.number(),
  center: z.tuple([z.number(), z.number()]),
  pitch: z.number(),
  type: z.literal(CAMERA_MESSAGE),
  zoom: z.number(),
});

/**
 * CameraMessage is the outbound camera reply, derived from cameraMessageSchema.
 */
export type CameraMessage = z.infer<typeof cameraMessageSchema>;

/**
 * inboundBridgeMessageSchema is the discriminated union (on `type`) of the
 * content-switch messages a parent window may post to the embed. setItinerary
 * activates an itinerary by id and carries the optional display overrides; the
 * union leaves room for further content-switch messages. The bridge Zod-parses
 * every window message against this before dispatching to the tool bus — the
 * "never trust the parent frame" boundary (CSP frame-ancestors + the publishable
 * key already gate WHO can post; this gates WHAT they post).
 */
export const inboundBridgeMessageSchema = z.discriminatedUnion("type", [
  switchOverridesSchema.extend({
    itineraryId: z.string(),
    type: z.literal(SET_ITINERARY_MESSAGE),
  }),
]);

/**
 * InboundBridgeMessage is one validated inbound content-switch message, derived
 * from inboundBridgeMessageSchema.
 */
export type InboundBridgeMessage = z.infer<typeof inboundBridgeMessageSchema>;

/**
 * readyErrorSchema enumerates the load-failure reasons the embed surfaces to its
 * parent: a load timeout or unresolved default content. null means a clean load.
 */
export const readyErrorSchema = z.enum(["timeout", "not_found"]).nullable();

/**
 * mapBoundsSchema is the [west, south, east, north] geographic bounds of the framed
 * itinerary posted with a ready event — the route's own extent, not the padded
 * viewport. null when no route frames the view (a base-map-only embed, a 2D/empty
 * itinerary that yields no extent) or the load settled with an error (no bounds to
 * report).
 */
export const mapBoundsSchema = z.tuple([z.number(), z.number(), z.number(), z.number()]).nullable();

/**
 * readyMessageSchema is the outbound ready payload the embed posts to its parent
 * once the map finishes loading. It carries the active content id, the resolved
 * terrain slug, the framed itinerary's geographic bounds (null when no route frames /
 * on error), and a stable error field (null on success) — this is the public
 * ready-event contract host integrations consume. The error field is always present
 * so hosts can rely on `error === null` for success rather than a missing key.
 */
export const readyMessageSchema = z.object({
  bounds: mapBoundsSchema,
  error: readyErrorSchema,
  itineraryId: z.string().nullable(),
  terrain: z.string(),
  type: z.literal(READY_MESSAGE),
});

/**
 * ReadyMessage is the outbound ready payload, derived from readyMessageSchema.
 */
export type ReadyMessage = z.infer<typeof readyMessageSchema>;
